import { Box, Typography, styled } from '@mui/material'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { IPositionDrawing } from 'src/generated-types/position-drawing'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { getConfigurePalette } from './configurePalette'
import { EmptyNote, PanelScrollArea } from './ConfigurePositionsPanel.style'
import DrawingThumb from './DrawingThumb'

interface DrawingPickerGridProps {
  drawings: IPositionDrawing[]
  loading: boolean
  pickedDrawingId: number | null
  onPick: (drawingId: number) => void
}

const DrawingPickerGrid: React.FC<DrawingPickerGridProps> = ({ drawings, loading, pickedDrawingId, onPick }) => {
  const { t } = useTranslation(['position', 'common'])

  const onTileKeyDown = (event: React.KeyboardEvent, drawingId: number) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    onPick(drawingId)
  }

  const renderContent = () => {
    if (loading) return <LoadingText>{t('loading', { ns: 'common' })}</LoadingText>
    if (drawings.length === 0) return <EmptyNote>{t('configure-no-match', { ns: 'position' })}</EmptyNote>
    return (
      <TileGrid>
        {drawings.map(drawing => {
          const isPicked = drawing.id === pickedDrawingId
          return (
            <DrawingTile
              key={drawing.id}
              role="button"
              tabIndex={0}
              aria-pressed={isPicked}
              picked={isPicked}
              onClick={() => onPick(drawing.id)}
              onKeyDown={event => onTileKeyDown(event, drawing.id)}
            >
              <TileImage>
                <DrawingThumb
                  url={drawing.fileUrl}
                  alt={drawing.drawingNumber}
                  selected={isPicked}
                  onSelect={() => onPick(drawing.id)}
                />
              </TileImage>
              <TileLabel>{drawing.drawingNumber}</TileLabel>
              {isPicked && <PickedMark aria-hidden>✓</PickedMark>}
            </DrawingTile>
          )
        })}
      </TileGrid>
    )
  }

  return <PanelScrollArea>{renderContent()}</PanelScrollArea>
}

export default DrawingPickerGrid

const LoadingText = styled(Typography)(({ theme }) => ({
  fontSize: 13,
  color: getThemeColor(theme, MUIThemeColors.secondaryDark),
}))

const TileGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: theme.spacing(1.25),
}))

const DrawingTile = styled(Box, { shouldForwardProp: prop => prop !== 'picked' })<{ picked: boolean }>(
  ({ theme, picked }) => {
    const palette = getConfigurePalette(theme)
    return {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.75),
      padding: picked ? '5px 5px 7px' : '6px 6px 8px',
      border: picked ? `2px solid ${palette.primary}` : `1px solid ${palette.line}`,
      borderRadius: 8,
      backgroundColor: picked ? palette.selectedSurface : palette.paper,
      cursor: 'pointer',
      textAlign: 'left',
      fontFamily: 'inherit',
      minWidth: 0,
    }
  },
)

const TileImage = styled(Box)(({ theme }) => ({
  aspectRatio: '16 / 10',
  borderRadius: 4,
  overflow: 'hidden',
  backgroundColor: getThemeColor(theme, MUIThemeColors.white),
}))

const TileLabel = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  padding: theme.spacing(0, 0.25),
  fontWeight: 600,
  fontSize: 14,
  color: getThemeColor(theme, MUIThemeColors.primaryDark),
}))

const PickedMark = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 10,
  right: 10,
  width: 22,
  height: 22,
  borderRadius: '50%',
  backgroundColor: getThemeColor(theme, MUIThemeColors.primaryMain),
  color: getThemeColor(theme, MUIThemeColors.white),
  display: 'grid',
  placeItems: 'center',
  fontSize: 14,
}))
